import React from 'react';

import Api from '~/Api';
import { PRODUCTS_PROJECT_CONFIG } from '~/constants/products';
import { useModalContext } from '~/contexts/modal/ModalContext';
import useErrors from '~/hooks/useErrors';
import { featureUnlockModalConfig } from '~/modules/feature-access/constants';
import { useFeatureAccess } from '~/modules/feature-access/hooks/useFeatureAccess';
import UpgradeSubModalFeatureUnlock from '~/modules/feature-access/modals/UpgradeSubModal';
import { FeatureKey, FeatureModule } from '~/modules/feature-access/policy';
import UpgradeSubscriptionCheckout, {
  singleScreenModalConfig,
} from '~/modules/settings/components/modals/UpgradeSubscriptionCheckout/variants/SingleScreen';
import { GB_FEATURE_KEYS } from '~/services/abTests/features';
import { growthBook } from '~/services/abTests/growthBook';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { ProductType } from '~/types/product';
import { timeout } from '~/utils/common';

const PURCHASE_SOURCE = 'sub_promo_button';

export const useUpgradeSubscription = (): {
  upgradeSubscription: () => Promise<void>;
} => {
  const { loadAndSetActualProfileData } = useActions();
  const { reportUserErrors } = useErrors();
  const { handleOpenModal } = useModalContext();
  const { getFeatureAccess } = useFeatureAccess();

  const upgradeSubscription = async (): Promise<void> => {
    try {
      const isNotPremium = !getFeatureAccess(
        FeatureModule.AI_CONTENT_DETECTOR,
        FeatureKey.DETECTOR_INPUT_LIMIT_OVERFLOW,
      );

      if (isNotPremium) {
        const isTest = growthBook.isTestGroup(
          GB_FEATURE_KEYS.PREMIUM_ANNUAL_MONTHLY_UPGRADE,
        );

        const results = await Promise.allSettled([
          Api.getProductById(PRODUCTS_PROJECT_CONFIG.ANNUAL_SUB_OFFER_ID),
          isTest
            ? Api.getProductById(PRODUCTS_PROJECT_CONFIG.MONTHLY_SUB_OFFER_ID)
            : Promise.resolve(null),
        ]);

        const products = results
          .filter((r): r is PromiseFulfilledResult<ProductType> => {
            if (r.status !== 'fulfilled') {
              return false;
            }
            return r.value !== null;
          })
          .map((r) => r.value);

        if (!products.length) {
          throw new Error('Both product requests failed');
        }

        analytics.trackEvent('subscription - change sub', {
          sub_id: products[0]?.id || '',
        });

        handleOpenModal({
          onClose: () =>
            analytics.trackEvent('platform checkout - close', {
              purchase_source: PURCHASE_SOURCE,
            }),
          ...featureUnlockModalConfig,
          component: ({ onClose }) => (
            <UpgradeSubModalFeatureUnlock
              products={products}
              purchaseSource={PURCHASE_SOURCE}
              onSuccessPayment={async () => {
                await timeout(4000);
                await loadAndSetActualProfileData();
                onClose();
              }}
            />
          ),
        });
      } else {
        const productById = await Api.getProductById(
          PRODUCTS_PROJECT_CONFIG.ANNUAL_SUB_OFFER_ID,
        );

        analytics.trackEvent('subscription - change sub', {
          sub_id: productById.id,
        });

        handleOpenModal({
          onClose: () =>
            analytics.trackEvent('platform checkout - close', {
              purchase_source: PURCHASE_SOURCE,
            }),
          ...singleScreenModalConfig,
          component: ({ onClose }) => (
            <UpgradeSubscriptionCheckout
              product={productById}
              purchaseSource={PURCHASE_SOURCE}
              onSuccessPayment={async () => {
                await timeout(4000);
                await loadAndSetActualProfileData();
                onClose();
              }}
            />
          ),
        });
      }
    } catch (e: any) {
      reportUserErrors({
        error: e,
        method: 'getProductById',
        userMessage:
          'Unable to fetch product details. Please retry or report issue',
      });
    }
  };

  return {
    upgradeSubscription,
  };
};
